import { useEffect, useState } from 'react';

import { IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';

import styles from './CompanySearch.module.css';
import { useGetCompanySearch } from '../../../../../api/companySearch/useGetCompanySearch';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { useStageFieldTracking } from '../../../form/MobileSignupFieldTracking';
import { StepLayout } from '../../../StepLayout/StepLayout';
import { TCompanyMatch } from '../companyData';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// DIT-680/681. Register search: debounced live lookup (300ms, min 3 chars) with
// an inline results list. Inline rather than the desktop Combobox, whose overlay
// fights the mobile keyboard. Query is controlled by the parent so it survives a
// round-trip back from the confirmation card. "Not listed" drops to manual entry.
export const CompanySearch = ({
  query,
  onQueryChange,
  onSelect,
  onEnterManually,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (company: TCompanyMatch) => void;
  onEnterManually: () => void;
}) => {
  const { t } = useLingui();
  const { fieldProps } = useStageFieldTracking();
  // Seed from the current query so a remount (e.g. returning from the confirmation
  // card) shows the cached results immediately instead of debouncing from empty.
  const [debouncedQuery, setDebouncedQuery] = useState(() =>
    query.length >= MIN_QUERY_LENGTH ? query : '',
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.length >= MIN_QUERY_LENGTH ? query : '');
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching, isError } = useGetCompanySearch({
    searchQuery: debouncedQuery,
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
  });

  const results = isError ? [] : (data?.data ?? []);
  const resultCount = results.length;
  const hasSearched = debouncedQuery.length >= MIN_QUERY_LENGTH;

  return (
    <StepLayout
      stageTitle={t`Business details`}
      heading={t`What’s your company called?`}
      subHeading={t`We search the commercial register and fill in your details automatically.`}
      hideCta
      fieldProps={fieldProps}
    >
      <div className={styles.searchScreen}>
        <Input
          iconLeft={<IconZoomOutline />}
          placeholder={t`Search a company name or number`}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          className="fs-mask"
          name="companyName"
          data-defer-completion="true"
        />
        {(isFetching || hasSearched) && (
          <div className={styles.searchStatus} role="status" aria-live="polite">
            {isFetching ? (
              <Spinner />
            ) : (
              plural(resultCount, {
                one: '# result',
                other: '# results',
              })
            )}
          </div>
        )}
        {!isFetching && results.length > 0 && (
          <ul
            className={styles.results}
            role="listbox"
            aria-label={t`Companies`}
          >
            {results.map((company) => (
              <li key={company.crefo_id} role="option" aria-selected={false}>
                <button
                  type="button"
                  onClick={() => onSelect(company)}
                  className={clsx(styles.result, 'fs-mask', 'dd-privacy-mask')}
                >
                  <b>{company.name}</b> {company.street} {company.house_number},{' '}
                  {company.postcode} {company.city}
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Only offered once the register has actually responded (with or
            without matches) — not while idle or mid-search. */}
        {hasSearched && !isFetching && (
          <ButtonLink
            type="button"
            className={styles.enterManually}
            onClick={onEnterManually}
          >
            {t`My company is not listed →`}
          </ButtonLink>
        )}
      </div>
    </StepLayout>
  );
};
